import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Zap, ShieldCheck } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const { login, verifyOtp } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null); // { hint }
  const [code, setCode] = useState("");

  const doLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await login(email, pw);
      if (r.mfa_required) {
        setMfa({ hint: r.mfa_email_hint });
        toast.info("Verification code sent to your email");
      } else {
        toast.success("Signed in");
        nav(r.redirect || "/", { replace: true });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await verifyOtp(email, code);
      toast.success("Signed in as Super Admin");
      nav(r.redirect || "/super-admin", { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center"><Zap className="w-5 h-5"/></div>
          <div className="text-xl font-bold">WaggleAI</div>
        </div>
        <div>
          <h1 className="text-5xl font-bold leading-tight tracking-tight mb-4">WhatsApp AI Sales<br/>on autopilot.</h1>
          <p className="text-emerald-100 text-lg max-w-md">Send. Engage. Qualify. Filter. Assign. Sell. Your AI sales assistant handles every conversation until a human closes.</p>
        </div>
        <div className="text-xs text-emerald-100/70">© 2026 WaggleAI · Multi-tenant SaaS · Meta WhatsApp Cloud API</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 border-slate-200 shadow-lg">
          {!mfa ? (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
                <p className="text-sm text-slate-500 mt-1">Sign in to your workspace</p>
              </div>
              <form onSubmit={doLogin} className="space-y-4">
                <div><Label>Email</Label><Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email-input"/></div>
                <div>
                  <div className="flex justify-between items-center">
                    <Label>Password</Label>
                    <Link to="/forgot-password" className="text-xs text-emerald-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                  <Input type="password" value={pw} onChange={(e)=>setPw(e.target.value)} required data-testid="login-password-input"/>
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="login-submit-button">
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
              <div className="mt-6 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
                No workspace yet? <Link to="/register" className="text-emerald-700 font-medium hover:underline" data-testid="register-link">Create one</Link>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700"><ShieldCheck className="w-5 h-5"/></div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Verify your identity</h2>
                  <p className="text-xs text-slate-500 mt-0.5">We sent a 6-digit code to <b>{mfa.hint}</b></p>
                </div>
              </div>
              <form onSubmit={doVerify} className="space-y-4">
                <div>
                  <Label>Verification code</Label>
                  <Input value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                    inputMode="numeric" autoFocus placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono" data-testid="otp-input"/>
                </div>
                <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="otp-verify-button">
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
              </form>
              <button className="mt-4 text-xs text-slate-500 hover:text-slate-700" onClick={()=>{setMfa(null);setCode("");}}>← Back to sign in</button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
